import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { useProjectStore } from '../../stores/projectStore';
import HomeEntryButtons from './HomeEntryButtons';
import HomeOverview from './HomeOverview';
import OnboardingFlow, { hasCompletedOnboarding, checkOnboardingStatus } from './OnboardingFlow';
import { formatDateTime } from '../../utils/formatDateTime';
import { useAuthActions } from '../../context/AuthContext';
import { hasNoWebAccess } from '../../utils/moduleAccess';

interface LandingPageProps {
    userName: string;
    userId: string;
    onNavigate: (
        destination: 'projects' | 'new-project' | 'quick-research' | 'settings',
        projectType?: 'research' | 'build',
    ) => void;
    onProjectSelect: (projectId: string) => void;
    /** Home actions (LandingRoute): 'regulatory_research' + query opens a
     *  research project seeded with the query. Required — the feed's
     *  "Ask BARK" has no fallback that keeps the question. */
    onHomeAction: (action: string, query: string, history?: Array<{ role: 'user' | 'assistant'; content: string }>) => Promise<void> | void;
    authToken?: string;
}

function formatDateStamp(d: Date): string {
    return formatDateTime(d, { context: 'stamp' });
}

export default function LandingPage({
    userName, userId, onNavigate, onProjectSelect, onHomeAction, authToken
}: LandingPageProps) {
    // BAR-1528: an account with every web module off (every free MCP signup,
    // and a paused or suspended organization) is refused by the orchestrator
    // and report-library on every web route (403 `web_access_not_in_plan`).
    // Its home page makes neither call: the onboarding tour shows web features
    // it cannot open, its status check would replace this whole page with an
    // error panel, and it has no projects to list.
    const { isModuleDisabled } = useAuthActions();
    const webAccessOff = hasNoWebAccess(isModuleDisabled);
    const [onboardingState, setOnboardingState] = useState<'checking' | 'show' | 'complete' | 'error'>(() => {
        if (webAccessOff || hasCompletedOnboarding(userId)) return 'complete';
        return authToken ? 'checking' : 'show';
    });
    const [onboardingError, setOnboardingError] = useState<string | null>(null);
    const [now, setNow] = useState(() => new Date());
    const { projectList, projectListUserId, listLoading, listFailedFor, loadProjectList } = useProjectStore();
    // The user the list was last requested for. Per user, not once per mount:
    // an impersonation start or exit changes the signed-in user while this
    // page stays mounted, and a once-only load kept the other identity's
    // projects in "Your work" until a reload.
    const fetchedFor = useRef<string | null>(null);
    const onboardingRequest = useRef(0);

    const resolveOnboarding = useCallback(async () => {
        const requestId = ++onboardingRequest.current;
        if (webAccessOff || hasCompletedOnboarding(userId)) {
            setOnboardingError(null);
            setOnboardingState('complete');
            return;
        }
        if (!authToken) {
            setOnboardingError(null);
            setOnboardingState('show');
            return;
        }

        setOnboardingError(null);
        setOnboardingState('checking');
        try {
            const completed = await checkOnboardingStatus(authToken, userId);
            if (requestId !== onboardingRequest.current) return;
            setOnboardingState(completed ? 'complete' : 'show');
        } catch (caughtError) {
            if (requestId !== onboardingRequest.current) return;
            setOnboardingError(
                caughtError instanceof Error ? caughtError.message : 'Could not check onboarding status',
            );
            setOnboardingState('error');
        }
    }, [authToken, userId, webAccessOff]);

    useEffect(() => {
        void resolveOnboarding();
    }, [resolveOnboarding]);

    useEffect(() => {
        // No web access: report-library refuses the list. `fetchedFor` stays
        // unset, so the list loads if the organization gains web access.
        if (webAccessOff) return;
        if (userId && fetchedFor.current !== userId && !listLoading) {
            fetchedFor.current = userId;
            // Home renders the top 12 in "Your work" — fetch a small page
            // (vs the default 100) and in LIGHT mode (skip the per-project
            // workspace download; the home only shows name/type/time). The
            // Project Library still loads its full list with counts.
            loadProjectList(userId, 1, false, 12, true);
        }
    }, [userId, listLoading, loadProjectList, webAccessOff]);

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 60_000);
        return () => clearInterval(t);
    }, []);

    if (onboardingState === 'checking') {
        return (
            <div className="h-full bg-bg flex items-center justify-center">
                <div role="status" className="flex items-center gap-3 text-sm text-fg-secondary">
                    <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                    Checking onboarding status…
                </div>
            </div>
        );
    }

    if (onboardingState === 'error') {
        return (
            <div className="h-full bg-bg flex items-center justify-center px-6">
                <div role="alert" className="max-w-md rounded-2xl border border-danger/30 bg-surface p-6 text-center shadow-sm">
                    <h1 className="text-xl font-semibold text-fg">Couldn’t load onboarding</h1>
                    <p className="mt-2 text-sm text-danger">{onboardingError}</p>
                    <button
                        type="button"
                        onClick={() => void resolveOnboarding()}
                        className="mt-5 min-h-8 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    >
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (onboardingState === 'show') {
        return (
            <OnboardingFlow
                key={userId}
                onComplete={() => setOnboardingState('complete')}
                authToken={authToken}
                userId={userId}
            />
        );
    }

    const hour = now.getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

    return (
        <div className="h-full bg-bg flex flex-col overflow-y-auto">
            <div className="flex-1 w-full max-w-[1360px] mx-auto px-8 pt-6 pb-10">
                {/* Hero stamp */}
                <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="relative inline-flex">
                        <span className="w-1.5 h-1.5 rounded-full bg-success" />
                        <span className="absolute inset-0 w-1.5 h-1.5 rounded-full bg-success-bg -m-[3px] ring-[3px] ring-success-bg" />
                    </span>
                    <span className="font-mono text-[11px] text-fg-tertiary uppercase tracking-wider tabular-nums">
                        {formatDateStamp(now)}
                    </span>
                </div>

                {/* Hero title */}
                <h1 className="font-heading text-[48px] leading-[1.12] tracking-[-0.02em] font-normal text-fg mb-6 text-center">
                    {greeting}, {userName}.
                    <br />
                    <span className="italic text-fg-secondary">
                        What are we figuring out today?
                    </span>
                </h1>

                {/* Lead */}
                <p className="font-body text-[17px] leading-[1.65] text-fg-secondary max-w-[680px] mb-8 text-center mx-auto">
                    Choose where to start. Chat gives quick answers with sources. Research is for in-depth
                    projects that build a cited report. Build turns regulation into models and artifacts.
                </p>

                {/* Entry points — Chat / Research / Build. This is where the
                    home chat composer sat until 2026-09-10; nobody chats on
                    the start page itself any more. Same width the composer
                    had, so the hero keeps its shape. */}
                <div className="mb-10 lg:mb-14 max-w-[880px] mx-auto">
                    <HomeEntryButtons />
                </div>

                <HomeOverview
                    authToken={authToken}
                    onAskBark={(query) => onHomeAction('regulatory_research', query)}
                    // Only a list loaded for this user: right after an
                    // impersonation exit the store still holds the other one.
                    projectList={projectListUserId === userId ? projectList : []}
                    projectsLoading={listLoading}
                    projectsError={!!userId && listFailedFor === userId}
                    onRetryProjects={() => { if (userId) loadProjectList(userId, 1, false, 12, true); }}
                    onProjectSelect={onProjectSelect}
                    onNavigate={onNavigate}
                />
            </div>
        </div>
    );
}
